import React, { forwardRef } from 'react';
import SpinLoader from '../Loader/SpinLoader';

/**
 * Modular Button component following Stride design system.
 *
 * @param {'primary'|'secondary'|'outline'|'ghost'|'danger'|'danger-outline'|'subtle'|'link'} variant
 * @param {'xs'|'sm'|'md'|'base'|'lg'|'xl'} size
 * @param {boolean} loading
 * @param {boolean} disabled
 * @param {boolean} disable - Alias for disabled (backward compatibility)
 * @param {React.ReactNode} leftIcon
 * @param {React.ReactNode} rightIcon
 * @param {boolean} fullWidth
 * @param {function} onClick
 * @param {function} handleClick - Alias for onClick (backward compatibility)
 */
const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  disable = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  onClick,
  handleClick,
  type = 'button',
  className = '',
  id,
  title,
  'aria-label': ariaLabel,
  ...rest
}, ref) => {
  const isDisabled = Boolean(disabled || disable || loading);
  const clickHandler = onClick || handleClick;

  // Variant classes
  const variantClasses = {
    primary:
      'bg-primary hover:bg-primary-hover active:bg-primary-active text-white shadow-sm hover:shadow focus:ring-4 focus:ring-primary/20 border border-transparent',
    secondary:
      'bg-white hover:bg-surface active:bg-primary-soft text-primary border border-border-hover hover:border-primary shadow-sm focus:ring-4 focus:ring-primary/15',
    outline:
      'bg-white hover:bg-gray-50 active:bg-gray-100 text-gray-700 border border-gray-300 hover:border-gray-400 shadow-sm focus:ring-4 focus:ring-gray-200',
    ghost:
      'bg-transparent hover:bg-gray-100 active:bg-gray-200 text-gray-700 border border-transparent focus:ring-2 focus:ring-gray-200',
    danger:
      'bg-danger hover:bg-red-700 active:bg-red-800 text-white shadow-sm hover:shadow focus:ring-4 focus:ring-red-200 border border-transparent',
    'danger-outline':
      'bg-white hover:bg-red-50 active:bg-red-100 text-danger border border-red-300 hover:border-danger shadow-sm focus:ring-4 focus:ring-red-100',
    subtle:
      'bg-primary-soft hover:bg-primary-soft-hover active:bg-primary-soft-active text-primary font-semibold border border-transparent focus:ring-4 focus:ring-primary/20',
    link:
      'bg-transparent text-primary hover:underline focus:underline p-0 h-auto border-0 shadow-none inline-flex',
  };

  // Size classes
  const sizeClasses = {
    xs: 'px-2.5 py-1 text-xs rounded-md gap-1 min-h-[28px] min-w-[80px]',
    sm: 'px-3 py-1.5 text-xs md:text-sm rounded-md gap-1.5 min-h-[34px] min-w-[100px]',
    md: 'px-4 py-2 text-sm rounded-lg gap-2 min-h-[40px]',
    base: 'px-5 py-2.5 text-sm rounded-lg gap-2 min-h-[42px] min-w-[120px]',
    lg: 'px-5 py-3 text-base rounded-lg gap-2.5 min-h-[46px] min-w-[120px]',
    xl: 'px-6 py-3.5 text-base rounded-lg gap-2.5 min-h-[50px] min-w-[120px]',
  };

  // Link variant does not need padding or min-height
  const appliedSizeClass = variant === 'link' ? 'text-sm gap-1' : (sizeClasses[size] || sizeClasses.md);
  const appliedVariantClass = variantClasses[variant] || variantClasses.primary;

  return (
    <button
      ref={ref}
      id={id}
      type={type}
      title={title}
      aria-label={ariaLabel}
      disabled={isDisabled}
      onClick={clickHandler}
      className={`
        inline-flex items-center justify-center font-medium transition-all duration-150 ease-in-out
        select-none focus:outline-none
        ${fullWidth ? 'w-full' : ''}
        ${appliedSizeClass}
        ${appliedVariantClass}
        ${isDisabled ? 'opacity-60 cursor-not-allowed shadow-none hover:shadow-none' : 'cursor-pointer'}
        ${className}
      `}
      {...rest}
    >
      {loading ? (
        <SpinLoader className="w-4 h-4 mr-2 shrink-0 animate-spin" />
      ) : leftIcon ? (
        <span className="shrink-0 flex items-center">{leftIcon}</span>
      ) : null}

      {children}

      {!loading && rightIcon && (
        <span className="shrink-0 flex items-center">{rightIcon}</span>
      )}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;

